import { EventRecordStatus } from "@/generated/model";

/**
 * What to say about a record's own sign-off, as opposed to the read-and-sign
 * requests raised against it.
 *
 * Four states rather than the obvious two. "Not acknowledged" is ambiguous on
 * its own: it covers a record still waiting for someone, a record that never
 * needed acknowledging, and a record closed without one, and telling a manager
 * the wrong one of those is worse than telling them nothing.
 */
export type RecordAcknowledgementState =
  /** Signed off. The record carries a permanent acknowledgement row. */
  | "ACKNOWLEDGED"
  /** Waiting for whoever the nursery says must acknowledge records of this type. */
  | "AWAITING"
  /**
   * Filed by someone who could resolve it themselves, so it skipped
   * acknowledgement and opened In progress.
   */
  | "NOT_REQUIRED"
  /** Reached a terminal state without an acknowledgement ever being recorded. */
  | "CLOSED_WITHOUT";

/**
 * Derived from the record's status plus whether an acknowledgement row exists.
 *
 * `IN_PROGRESS` with no row can only mean the record skipped acknowledgement:
 * one that needed it would sit at `AWAITING_ACKNOWLEDGEMENT` until acknowledged,
 * and acknowledging always writes the row. That inference is why this does not
 * need a `requiresAcknowledgement` flag on the payload.
 *
 * Status is used rather than the row's absence because status is derived from
 * the record's event log. A later change to the nursery's rules cannot move a
 * record back into waiting — what it required is what it required when filed.
 */
export function recordAcknowledgementState(
  status: EventRecordStatus,
  hasAcknowledgement: boolean,
): RecordAcknowledgementState {
  if (hasAcknowledgement) {
    return "ACKNOWLEDGED";
  }
  if (status === EventRecordStatus.AWAITING_ACKNOWLEDGEMENT) {
    return "AWAITING";
  }
  if (
    status === EventRecordStatus.RESOLVED ||
    status === EventRecordStatus.ARCHIVED
  ) {
    return "CLOSED_WITHOUT";
  }
  return "NOT_REQUIRED";
}
